import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Ref } from '@eastsideco/salvo-ts';
import type { DrawerContainer } from '../Drawer/DrawerContainer';

@Component
export class QuickAddSelection extends ThemeComponent {
    @Ref({required: false}) quickAddSelection!: HTMLButtonElement;

    private drawerContainer: DrawerContainer | null = null;
    private submitButtons: HTMLButtonElement[] | null = [];
    private isAbsoluter = document.querySelector<HTMLElement>('.has-absoluter-status');

    init() {
        // Set the drawer container
        this.drawerContainer = document.querySelector('.quick-add-drawer');

        if (this.quickAddSelection) {
            this.$listen(this.quickAddSelection, 'click', async() => {
                const body = document.querySelector<HTMLElement>('body');
                body?.classList.add('prevent-scroll');

                const parentCard = this.quickAddSelection.closest('.s-product-card') as HTMLElement;
                if (!parentCard) {return;}

                const buttonType = this.quickAddSelection.getAttribute('data-product-type') || "";

                void await this.handlePurchaseOptions(parentCard, buttonType);

                this.drawerContainer?.open();
            });
        }
    }

    async handlePurchaseOptions(parentCard: HTMLElement, buttonType: string) {
        const purchaseOptions = this.drawerContainer?.querySelector<HTMLElement>('.quick-add-drawer__purchase-options');
        if (!purchaseOptions) {return;}

        // Get data attributes
        const dataAttrs = ['data-featured-image', 'data-handle', 'data-fine-print', 'data-delivery-details'];
        const [attrFeaturedImage, attrProductHandle, attrFinePrint, attrDeliveryDetails] =
            dataAttrs.map((attr) => parentCard.getAttribute(attr) || "");

        // Fetch product section
        const productUrl = `${window.location.origin}/products/${attrProductHandle}`;
        const res = await this.fetchProductMainSection(productUrl);

        // Create temp div elem to get product-main html for drawer
        const tempQuickAddSection = document.createElement('div');
        tempQuickAddSection.innerHTML = res['product-main'];

        const queries = {
            titleText: tempQuickAddSection.querySelector('h1')?.textContent || "",
            stampedStars: tempQuickAddSection.querySelector<HTMLElement>('.stamped-stars__wrapper'),
            subScriptionForm: tempQuickAddSection.querySelector<HTMLElement>('.subscription-form'),
            productForm: tempQuickAddSection.querySelector<HTMLElement>('.product-form'),
            radioInputs: Array.from(tempQuickAddSection.querySelectorAll(
                '.product-form__subscription-choice'
            )) as HTMLInputElement[],
            submitButtons: Array.from(tempQuickAddSection.querySelectorAll(
                'button[data-ref="submitButton"]'
            )) as HTMLButtonElement[]
        };

        this.submitButtons = queries.submitButtons;

        if (buttonType == 'one-time') {
            this.handleOneTimeOption(queries.radioInputs);
        }

        if (attrFeaturedImage) { this.handleFeaturedImage(productUrl, attrFeaturedImage, queries.titleText);}
        if (queries.titleText) { this.handleTitle(productUrl, queries.titleText);}
        if (queries.stampedStars) {this.handleReviews(queries.stampedStars);}
        if (queries.subScriptionForm) {this.handleSubscritptionFormOptions(queries.subScriptionForm, purchaseOptions);}
        if (queries.productForm) {this.handleProductFormOptions(queries.productForm, purchaseOptions);}
        if (attrFinePrint) { this.handleFinePrint(attrFinePrint, purchaseOptions); }
        if (attrDeliveryDetails) { this.handleDeliveryDetails(attrDeliveryDetails, purchaseOptions); }
    }

    // Updates button with one time data
    private handleOneTimeOption(radioInputsArray: HTMLInputElement[]) {
        if (radioInputsArray) {
            for(const radio of radioInputsArray) {
                (radio as HTMLInputElement).checked = false;

                const label = radio.parentElement?.closest('label');
                label?.classList.remove('radio-selection--checked');
                const labelFor = label?.getAttribute('for');

                if(labelFor) {
                    if (labelFor?.includes('frequency-0')) {
                        this.handleInputOnChange(radio as HTMLInputElement);
                    }
                }
            }
        }
    }

    // Updates the featured image in the drawer
    private handleFeaturedImage(productUrl: string, attrFeaturedImage: string, titleText: string) {

        const drawerFeatImage = this.drawerContainer?.querySelector('.quick-add__featured-image');
        if (!drawerFeatImage) {return;}

        drawerFeatImage.innerHTML = "";
        const imageSrc = `${attrFeaturedImage}`;

        const linkElement = Object.assign(document.createElement('a'), { href: productUrl });
        const drawerImage = Object.assign(new Image(), { src: imageSrc, alt: titleText || "", width: '200' });

        linkElement.appendChild(drawerImage);
        drawerFeatImage.appendChild(linkElement);
    }

    // Updates the title in the drawer
    private handleTitle(productUrl: string, titleText: string) {
        const drawerTitle = this.drawerContainer?.querySelector('.quick-add__title');
        if (drawerTitle && titleText) {
            drawerTitle.innerHTML = `<a href="${productUrl}">${titleText}</a>`;
        }
    }

    // Updates the reviews section in the drawer
    private handleReviews(stampedStars: HTMLElement) {
        const drawerRating = this.drawerContainer?.querySelector('.quick-add__rating');
        if (drawerRating && stampedStars) {
            drawerRating.innerHTML = '';
            drawerRating?.appendChild(stampedStars);
        }
    }

    // Updates the subscription form purchase options in the drawer
    private handleSubscritptionFormOptions(subScriptionForm: HTMLElement, purchaseOptions: HTMLElement) {
        purchaseOptions.innerHTML = '';
        purchaseOptions.appendChild(subScriptionForm);
    }

    // Updates the product form purchase options in the drawer
    private handleProductFormOptions(productForm: HTMLElement, purchaseOptions: HTMLElement) {
        purchaseOptions.innerHTML = '';
        purchaseOptions.appendChild(productForm);
    }

    // Adds fine print information to the purchase options in the drawer
    private handleFinePrint(attrFinePrint: string, purchaseOptions: HTMLElement) {
        purchaseOptions.insertAdjacentHTML(
            'beforeend',
            `<div class="quick-add-drawer__fine-print">${attrFinePrint}</div>`
        );
    }

    // Adds delivery details to the purchase options in the drawer
    private handleDeliveryDetails(attrDeliveryDetails: string, purchaseOptions: HTMLElement) {
        purchaseOptions.insertAdjacentHTML(
            'beforeend',
            `<div class="quick-add-drawer__delivery-info">${attrDeliveryDetails}</div>`
        );
    }

    // Handles changes in the radio input selection for 'one time'
    private handleInputOnChange(radioInput: HTMLInputElement) {
        radioInput.checked = true;
        radioInput.closest('label')?.classList.add('radio-selection--checked');

        const radioPrice = (Number(radioInput.getAttribute('data-price') || 0) / 100).toFixed(2);
        const radioQuantity = radioInput.getAttribute('data-quantity') || '';
        const radioFreeGift = radioInput.getAttribute('data-has-gift') || '';

        if(this.submitButtons) {
            for (const button of this.submitButtons) {
                button.setAttribute('quantity', radioQuantity);
                button.setAttribute('has_gift', radioFreeGift);
                button.setAttribute('selling_plan', '');
                button.setAttribute('unit_type', '');

                const price = button.querySelector('.product-form__selected-price');
                if (price) {
                    price.textContent = `£${radioPrice}`;
                }
            }
        }
    }

    // Fetches the product main section from the server
    private async fetchProductMainSection(productUrl: string) {
        const url = new URL(productUrl)
        url.searchParams.set('sections','product-main');
        return await fetch(url).then(response => response.json());
    }
}
